export var ErrorCode;
(function (ErrorCode) {
    ErrorCode[ErrorCode["InternalPlayerError"] = 1000] = "InternalPlayerError";
    ErrorCode[ErrorCode["MissingContentSource"] = 1001] = "MissingContentSource";
    ErrorCode[ErrorCode["UnknownAdProvider"] = 1002] = "UnknownAdProvider";
    ErrorCode[ErrorCode["NotAllowedError"] = 1003] = "NotAllowedError";
    ErrorCode[ErrorCode["NotSupportedError"] = 1004] = "NotSupportedError";
    ErrorCode[ErrorCode["MediaErrorAborted"] = 1005] = "MediaErrorAborted";
    ErrorCode[ErrorCode["MediaErrorDecode"] = 1006] = "MediaErrorDecode";
    ErrorCode[ErrorCode["MediaErrorNetwork"] = 1007] = "MediaErrorNetwork";
    ErrorCode[ErrorCode["MediaErrorSourceNotSupported"] = 1008] = "MediaErrorSourceNotSupported";
    ErrorCode[ErrorCode["MixedContentUpgradeError"] = 1009] = "MixedContentUpgradeError";
    ErrorCode[ErrorCode["PlaylistParseError"] = 1010] = "PlaylistParseError";
    ErrorCode[ErrorCode["RequestTimeout"] = 1011] = "RequestTimeout";
    ErrorCode[ErrorCode["LoadError"] = 1012] = "LoadError";
})(ErrorCode || (ErrorCode = {}));
export var ErrorType;
(function (ErrorType) {
    ErrorType["SETUP_ERROR"] = "SetupError";
    ErrorType["LOAD_ERROR"] = "LoadError";
    ErrorType["PLAY_ERROR"] = "PlayError";
})(ErrorType || (ErrorType = {}));
export var ErrorName;
(function (ErrorName) {
    ErrorName["InternalPlayerError"] = "InternalPlayerError";
    ErrorName["MediaErrorAborted"] = "MediaErrorAborted";
    ErrorName["MediaErrorDecode"] = "MediaErrorDecode";
    ErrorName["MediaErrorNetwork"] = "MediaErrorNetwork";
    ErrorName["MediaErrorSourceNotSupported"] = "MediaErrorSourceNotSupported";
    ErrorName["NotAllowedError"] = "NotAllowedError";
    ErrorName["NotSupportedError"] = "NotSupportedError";
    ErrorName["MissingContentSource"] = "MissingContentSource";
    ErrorName["UnknownAdProvider"] = "UnknownAdProvider";
    ErrorName["MixedContentUpgradeError"] = "MixedContentUpgradeError";
    ErrorName["PlaylistParseError"] = "PlaylistParseError";
    ErrorName["RequestTimeout"] = "RequestTimeout";
    ErrorName["LoadError"] = "LoadError";
})(ErrorName || (ErrorName = {}));
export const MediaErrorMap = new Map(typeof MediaError !== 'undefined'
    ? [
        [MediaError.MEDIA_ERR_ABORTED, ErrorName.MediaErrorAborted],
        [MediaError.MEDIA_ERR_DECODE, ErrorName.MediaErrorDecode],
        [MediaError.MEDIA_ERR_NETWORK, ErrorName.MediaErrorNetwork],
        [
            MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED,
            ErrorName.MediaErrorSourceNotSupported,
        ],
    ]
    : []);
const errors = {
    [ErrorName.InternalPlayerError]: {
        message: 'Internal player error',
        code: ErrorCode.InternalPlayerError,
        type: ErrorType.PLAY_ERROR,
        name: ErrorName.InternalPlayerError,
        fatal: true,
    },
    [ErrorName.MediaErrorAborted]: {
        message: 'Media error aborted',
        code: ErrorCode.MediaErrorAborted,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MediaErrorAborted,
        fatal: true,
    },
    [ErrorName.MediaErrorDecode]: {
        message: 'Media error decode',
        code: ErrorCode.MediaErrorDecode,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MediaErrorDecode,
        fatal: true,
    },
    [ErrorName.MediaErrorNetwork]: {
        message: 'Media error network',
        code: ErrorCode.MediaErrorNetwork,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MediaErrorNetwork,
        fatal: true,
    },
    [ErrorName.MediaErrorSourceNotSupported]: {
        message: 'Media error source not supported',
        code: ErrorCode.MediaErrorSourceNotSupported,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MediaErrorSourceNotSupported,
        fatal: true,
    },
    [ErrorName.NotAllowedError]: {
        message: 'User agent disallowed playback',
        code: ErrorCode.NotAllowedError,
        type: ErrorType.PLAY_ERROR,
        name: ErrorName.NotAllowedError,
        fatal: true,
    },
    [ErrorName.NotSupportedError]: {
        message: "Media source doesn't represent a supported media format",
        code: ErrorCode.NotSupportedError,
        type: ErrorType.PLAY_ERROR,
        name: ErrorName.NotSupportedError,
        fatal: true,
    },
    [ErrorName.MissingContentSource]: {
        message: 'Missing content source',
        code: ErrorCode.MissingContentSource,
        type: ErrorType.SETUP_ERROR,
        name: ErrorName.MissingContentSource,
        fatal: true,
    },
    [ErrorName.UnknownAdProvider]: {
        message: 'Unknown ad provider',
        code: ErrorCode.UnknownAdProvider,
        type: ErrorType.SETUP_ERROR,
        name: ErrorName.UnknownAdProvider,
        fatal: true,
    },
    [ErrorName.MixedContentUpgradeError]: {
        message: 'Could not upgrade the source to HTTPS',
        code: ErrorCode.MixedContentUpgradeError,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.MixedContentUpgradeError,
        fatal: true,
    },
    [ErrorName.PlaylistParseError]: {
        message: 'Failed to parse a playlist due to malformed data',
        code: ErrorCode.PlaylistParseError,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.PlaylistParseError,
        fatal: true,
    },
    [ErrorName.RequestTimeout]: {
        message: 'Request Failed',
        code: ErrorCode.RequestTimeout,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.RequestTimeout,
        fatal: false,
    },
    [ErrorName.LoadError]: {
        message: 'Failed to load any source',
        code: ErrorCode.LoadError,
        type: ErrorType.LOAD_ERROR,
        name: ErrorName.LoadError,
        fatal: true,
    },
};
export class PlayerError {
    code;
    type;
    fatal;
    message;
    name;
    constructor(msg, code, type, name, fatal) {
        this.message = msg;
        this.code = code;
        this.type = type;
        this.name = name;
        this.fatal = fatal;
    }
    static createError(error, message) {
        let name;
        let customMessage;
        if (error instanceof PlayerError) {
            return error;
        }
        if (!error) {
            name = ErrorName.InternalPlayerError;
        }
        else if (error instanceof MediaError) {
            name =
                MediaErrorMap.get(error?.code) ??
                    ErrorName.InternalPlayerError;
            customMessage =
                error?.message || `Unknown MediaError. Error code: ${error?.code}`;
        }
        else if (typeof error === 'string') {
            name = error;
        }
        else {
            name = error.name;
        }
        const { message: playerErrorMessage, code, type, fatal, } = PlayerError.getErrorDetails(name);
        return new PlayerError(message || customMessage || playerErrorMessage || 'Unknown Error', code, type, name, fatal);
    }
    static getErrorDetails(name) {
        return (errors[name] ?? {
            name,
        });
    }
}
